import { useState } from 'react';
import { Button } from '../ui/Button';
import { usePlatform } from '../platform/context';
import { useExport } from './useExport';

/**
 * The one screen that matters if this machine ever dies.
 *
 * Writing the file is all this does. Reading one back is `RestoreSection` —
 * they're two different intentions, and a person arriving here already knows
 * which of the two they came for.
 *
 * "Show Data Folder" sits here rather than in a section of its own because
 * what it opens is the thing the button above it copies.
 */
export function BackupSection() {
  const platform = usePlatform();
  const exporter = useExport();
  const [folderError, setFolderError] = useState<string | undefined>(undefined);

  // Captured once so the check below narrows it for the call inside `reveal`.
  const showDataFolder = platform.showDataFolder;

  async function reveal() {
    if (showDataFolder === undefined) return;
    setFolderError(undefined);
    try {
      await showDataFolder();
    } catch {
      setFolderError("That folder couldn't be opened.");
    }
  }

  return (
    <section className="settings__section">
      <h2 className="section-label">Backup</h2>
      <p className="settings__note">
        Everything your VisiList holds, in one <code>.visilist</code> file — rooms, descriptions,
        notes, prices, and every photo at full size. It’s a plain zip, so the pictures are still
        there if this app isn’t. Export a backup now.
      </p>

      <div className="settings__actions">
        <Button onClick={() => void exporter.run()} disabled={exporter.busy}>
          {exporter.busy ? 'Exporting…' : 'Export Everything'}
        </Button>
        {showDataFolder !== undefined && (
          <Button variant="quiet" onClick={() => void reveal()}>
            Show Data Folder
          </Button>
        )}
      </div>

      {exporter.saved !== undefined && (
        <p className="settings__result" role="status">
          Saved as {exporter.saved}.
        </p>
      )}

      {exporter.error !== undefined && (
        <p className="settings__result" role="alert">
          {exporter.error}
        </p>
      )}

      {folderError !== undefined && (
        <p className="settings__result" role="alert">
          {folderError}
        </p>
      )}
    </section>
  );
}
